import type { PurchaseOrderTimeline } from '@/components/delivery-timeline'

/**
 * Date math for the project's expected-timeline chart (USA-1432). Dates are calendar days
 * ("YYYY-MM-DD"); they become whole day numbers so nothing shifts with the viewer's time zone.
 */

const DAY_MS = 86_400_000
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** Days a PO waited before it went out that read as time lost (credit requests, spec questions). */
export const LONG_WAIT_DAYS = 3
/** At most this many days across the axis use weekly ticks; longer spans tick by month. */
const WEEKLY_TICKS_MAX_DAYS = 84

export type Status = PurchaseOrderTimeline['status']

/** "2026-10-05" → whole days since 1970-01-01 for that calendar date. */
export function dayNumber(value: string): number {
  const [year, month, day] = value.slice(0, 10).split('-').map(Number)
  return Math.round(Date.UTC(year ?? 1970, (month ?? 1) - 1, day ?? 1) / DAY_MS)
}

/** Today's local calendar date as a day number. */
export function todayNumber(now: Date = new Date()): number {
  return Math.round(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()) / DAY_MS)
}

function parts(day: number) {
  const date = new Date(day * DAY_MS)
  return { year: date.getUTCFullYear(), month: date.getUTCMonth(), date: date.getUTCDate(), weekday: date.getUTCDay() }
}

/** Day number → "Oct 5". */
export function dayLabel(day: number): string {
  const { month, date } = parts(day)
  return `${MONTHS[month]} ${date}`
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

/** Day number → "Mon, Nov 2". */
export function weekdayLabel(day: number): string {
  return `${WEEKDAYS[parts(day).weekday]}, ${dayLabel(day)}`
}

/** The business day after a day number (weekends skipped). */
export function nextBusinessDay(day: number): number {
  let next = day + 1
  while ([0, 6].includes(new Date(next * DAY_MS).getUTCDay())) next += 1
  return next
}

/** A day number ``count`` business days later. */
export function addBusinessDays(day: number, count: number): number {
  let result = day
  for (let index = 0; index < count; index += 1) result = nextBusinessDay(result)
  return result
}

function maybeDay(value: string | null | undefined): number | null {
  return value ? dayNumber(value) : null
}

/** Waiting to send, waiting on a spec credit request, with the manufacturer, in transit. */
export type SegmentKind = 'waiting' | 'spec' | 'lead' | 'transit'

export type Segment = {
  kind: SegmentKind
  start: number
  end: number
  /** Percent of the axis from the left edge. */
  left: number
  /** Percent of the axis. */
  width: number
  /** Right of today: what is still expected, drawn faded. */
  forecast: boolean
  /** Waiting segment only: long enough to count as time lost. */
  long: boolean
}

/** A small break in the bar where one phase hands over to the next. */
export type Marker = { kind: 'sent' | 'ships'; day: number; left: number }

/** The at-a-glance answer in the right-hand column. */
export type Arrival = {
  /** "Mon, Nov 2", "Delivered Oct 20", or "No date yet". */
  text: string
  /** A lead-time estimate rather than a manufacturer or carrier date. */
  est: boolean
  /** "+3 days late" or "at risk"; null when on track or delivered. */
  note: string | null
}

export type Row = {
  timeline: PurchaseOrderTimeline
  segments: Segment[]
  markers: Marker[]
  arrival: Arrival
  /** Days the arrival lands after the deliver-by date (else after the plan); null when unknown. */
  daysLate: number | null
  /** Days between ordering and sending (or ordering and today while unsent). */
  waitDays: number
  /** Days of that wait spent on the spec credit request; null without one. */
  specDays: number | null
  /** An unsent PO's expected send day: the business day after it was ordered, or today once that passed. */
  expectedSend: number | null
  /** Today's estimate differs from the plan at placement. */
  plannedArrival: number | null
}

export type Tick = { day: number; left: number; label: string; major: boolean }

export type Rule = { day: number; left: number; label: string; align: 'start' | 'end' }

export type ExpectedTimeline = {
  start: number
  end: number
  ticks: Tick[]
  today: Rule | null
  deliverBy: Rule | null
  rows: Row[]
  worst: Status
  /** "Last to arrive: Matfer, Fri, Nov 20 · 3 not sent yet"; null once everything is delivered. */
  summary: string | null
}

const RANK: Record<Status, number> = { delivered: 0, on_track: 1, at_risk: 2, late: 3 }

/** The worst status among open POs; "delivered" once everything has arrived. */
export function worstStatus(timelines: PurchaseOrderTimeline[]): Status {
  return timelines.reduce<Status>(
    (worst, timeline) => (RANK[timeline.status] > RANK[worst] ? timeline.status : worst),
    'delivered',
  )
}

/** Today's ship date, falling back to the plan. */
function shipDay(t: PurchaseOrderTimeline) {
  return maybeDay(t.expected_ship_on) ?? maybeDay(t.planned_ship_on)
}

/** Today's arrival date, falling back to the plan. */
function arrivalDay(t: PurchaseOrderTimeline) {
  return maybeDay(t.expected_arrival_on) ?? maybeDay(t.planned_arrival_on)
}

/** Sort key: expected arrival first; POs with no arrival date last. */
function sortKey(t: PurchaseOrderTimeline): [number, number] {
  const arrival = arrivalDay(t) ?? shipDay(t)
  return [arrival ?? Number.MAX_SAFE_INTEGER, dayNumber(t.ordered_on)]
}

/** Every PO has arrived: the timeline ends on the last delivery rather than running on to today (USA-1498). */
export function allDelivered(timelines: PurchaseOrderTimeline[]): boolean {
  return timelines.length > 0 && timelines.every((t) => t.status === 'delivered')
}

/**
 * The axis range: ordering through the latest date that matters, plus a little padding. Today is
 * on it while anything is still coming; a fully delivered project's axis ends with its last arrival.
 */
export function axisRange(timelines: PurchaseOrderTimeline[], today: number): { start: number; end: number } {
  const now = allDelivered(timelines) ? [] : [today]
  const starts = [...now, ...timelines.map((t) => dayNumber(t.ordered_on))]
  const ends = [
    ...now,
    ...timelines.flatMap((t) =>
      [t.deliver_by, t.expected_ship_on, t.expected_arrival_on, t.planned_ship_on, t.planned_arrival_on, t.sent_on].map(
        maybeDay,
      ),
    ),
  ].filter((day): day is number => day !== null)
  let start = Math.min(...starts)
  let end = Math.max(...ends)
  // Never narrower than two weeks, so a short project does not stretch into a few huge bars.
  if (end - start < 14) end = start + 14
  const pad = Math.max(2, Math.round((end - start) * 0.04))
  start -= pad
  end += pad
  return { start, end }
}

/** Week ticks (Mondays) for short spans, month ticks for long ones; alternating ticks are major. */
export function axisTicks(start: number, end: number): Tick[] {
  const span = end - start
  const place = (day: number) => ((day - start) / span) * 100
  const days: { day: number; label: string }[] = []
  if (span <= WEEKLY_TICKS_MAX_DAYS) {
    const { weekday } = parts(start)
    // Next Monday on or after the start.
    for (let day = start + ((8 - weekday) % 7); day <= end; day += 7) {
      days.push({ day, label: dayLabel(day) })
    }
  } else {
    let { year, month } = parts(start)
    for (;;) {
      month += 1
      if (month > 11) {
        month = 0
        year += 1
      }
      const day = Math.round(Date.UTC(year, month, 1) / DAY_MS)
      if (day > end) break
      days.push({ day, label: month === 0 ? `${MONTHS[0]} ’${String(year).slice(2)}` : (MONTHS[month] ?? '') })
    }
  }
  return days
    .map(({ day, label }) => ({ day, label, left: place(day) }))
    // Labels sit to the right of their tick; drop the ones that would run off the end.
    .filter((tick) => tick.left <= 94)
    .map((tick, index) => ({ ...tick, major: index % 2 === 0 }))
}

/** One phase, split at today into what has happened and what is still expected. */
function phase(
  kind: SegmentKind,
  start: number,
  end: number,
  axis: { start: number; end: number },
  today: number,
  options: { done: boolean; long?: boolean },
): Segment[] {
  if (end < start) return []
  const span = axis.end - axis.start
  const piece = (from: number, to: number, forecast: boolean): Segment => ({
    kind,
    start: from,
    end: to,
    left: ((from - axis.start) / span) * 100,
    // A same-day phase still gets a sliver so it is visible.
    width: Math.max(((to - from) / span) * 100, 0.6),
    forecast,
    long: options.long ?? false,
  })
  if (options.done || end <= today) return [piece(start, end, false)]
  if (start >= today) return [piece(start, end, true)]
  return [piece(start, today, false), piece(today, end, true)]
}

function plural(count: number, word: string) {
  return `${count} ${word}${count === 1 ? '' : 's'}`
}

function arrivalText(t: PurchaseOrderTimeline, arrival: number | null, daysLate: number | null): Arrival {
  if (t.status === 'delivered') {
    return { text: arrival === null ? 'Delivered' : `Delivered ${dayLabel(arrival)}`, est: false, note: null }
  }
  const note =
    t.status === 'late'
      ? daysLate !== null && daysLate > 0
        ? `+${plural(daysLate, 'day')} late`
        : 'late'
      : t.status === 'at_risk'
        ? 'at risk'
        : null
  if (arrival === null) return { text: 'No date yet', est: false, note }
  return { text: weekdayLabel(arrival), est: !t.firm, note }
}

/** One PO: waiting to send, with the manufacturer, and in transit, split at today. */
export function rowSegments(t: PurchaseOrderTimeline, axis: { start: number; end: number }, today: number): Row {
  const delivered = t.status === 'delivered'
  const span = axis.end - axis.start
  const ordered = dayNumber(t.ordered_on)
  const sent = maybeDay(t.sent_on)
  const ship = shipDay(t)
  const arrival = arrivalDay(t)
  const segments: Segment[] = []
  const markers: Marker[] = []
  const mark = (kind: Marker['kind'], day: number) => markers.push({ kind, day, left: ((day - axis.start) / span) * 100 })

  // An unsent PO is expected to go out the business day after it was ordered (our processing day,
  // which covers the spec credit request), or today once that has passed. The wait runs to then,
  // faded past today, and the manufacturer's lead time starts after it.
  const specSent = maybeDay(t.spec_credit_sent_on)
  const specDeadline = maybeDay(t.spec_credit_deadline_on)
  const expectedSend = sent === null && !delivered ? Math.max(today, nextBusinessDay(ordered)) : null
  const waitEnd = sent ?? expectedSend ?? ordered
  const waitDays = waitEnd - ordered
  // From the spec credit request going out (or being due to) until the PO itself goes out, it waits on it.
  const specFrom = specSent ?? (sent === null && specDeadline !== null ? ordered : null)
  const specStart = specFrom !== null && specFrom <= waitEnd ? Math.max(specFrom, ordered) : null
  const specDays = specStart !== null ? waitEnd - specStart : null
  const processingEnd = specStart ?? waitEnd
  if (processingEnd > ordered) {
    const days = processingEnd - ordered
    segments.push(
      ...phase('waiting', ordered, processingEnd, axis, today, { done: sent !== null, long: days >= LONG_WAIT_DAYS }),
    )
  }
  if (specStart !== null && waitEnd >= specStart) {
    segments.push(...phase('spec', specStart, waitEnd, axis, today, { done: sent !== null }))
  }

  const leadStart = Math.max(waitEnd, ordered)
  const leadEnd = ship ?? arrival
  if (leadEnd !== null && leadEnd >= leadStart) {
    if (waitDays > 0) mark('sent', leadStart)
    segments.push(...phase('lead', leadStart, leadEnd, axis, today, { done: delivered || t.shipped }))
  }
  const transitStart = ship !== null ? Math.max(ship, leadStart) : null
  if (transitStart !== null && arrival !== null && arrival >= transitStart) {
    if (segments.some((s) => s.kind === 'lead') && arrival > transitStart) mark('ships', transitStart)
    segments.push(...phase('transit', transitStart, arrival, axis, today, { done: delivered }))
  }

  const deliverBy = maybeDay(t.deliver_by)
  const planned = maybeDay(t.planned_arrival_on)
  // Late is measured against the customer's date, else against the plan at placement (as the backend does).
  const against = deliverBy ?? planned
  const daysLate = against !== null && arrival !== null ? arrival - against : null
  const plannedArrival = !delivered && planned !== null && planned !== maybeDay(t.expected_arrival_on) ? planned : null
  return {
    timeline: t,
    segments,
    markers,
    arrival: arrivalText(t, arrival, daysLate),
    daysLate,
    waitDays,
    specDays,
    expectedSend,
    plannedArrival,
  }
}

function names(list: string[]) {
  if (list.length <= 2) return list.join(' and ')
  return `${list[0]} and ${list.length - 1} more`
}

/** Which open PO arrives last, and how many have not gone to the manufacturer yet. */
export function timelineSummary(rows: Row[]): string | null {
  const open = rows.filter((row) => row.timeline.status !== 'delivered')
  if (!open.length) return null
  const parts: string[] = []
  const dated = open
    .map((row) => ({ row, day: arrivalDay(row.timeline) }))
    .filter((item): item is { row: Row; day: number } => item.day !== null)
  if (dated.length > 1) {
    const last = Math.max(...dated.map((item) => item.day))
    const latest = dated
      .filter((item) => item.day === last)
      .map(({ row }) => row.timeline.manufacturer_name || row.timeline.po_number)
    parts.push(`Last to arrive: ${names([...new Set(latest)])}, ${weekdayLabel(last)}`)
  }
  const unsent = open.filter((row) => !row.timeline.sent_on).length
  if (unsent) parts.push(unsent === open.length ? 'Not sent to the manufacturer yet' : `${unsent} not sent yet`)
  return parts.length ? parts.join(' · ') : null
}

/** Everything the chart draws: axis, ticks, Today and deliver-by rules, and one row per PO. */
export function buildExpectedTimeline(
  timelines: PurchaseOrderTimeline[],
  today: number = todayNumber(),
): ExpectedTimeline {
  const axis = axisRange(timelines, today)
  const span = axis.end - axis.start
  const place = (day: number) => ((day - axis.start) / span) * 100
  const deliverByDays = timelines.map((t) => maybeDay(t.deliver_by)).filter((day): day is number => day !== null)
  const deliverByDay = deliverByDays.length ? Math.max(...deliverByDays) : null

  const todayRule: Rule | null = allDelivered(timelines)
    ? null
    : { day: today, left: place(today), label: 'Today', align: 'end' }
  let deliverRule: Rule | null = null
  if (deliverByDay !== null) {
    deliverRule = {
      day: deliverByDay,
      left: place(deliverByDay),
      label: `Deliver by ${dayLabel(deliverByDay)}`,
      align: 'start',
    }
    // The left-hand label hangs left of its rule, the right-hand one right of it, so they never cross.
    if (todayRule && deliverByDay < today) {
      deliverRule.align = 'end'
      todayRule.align = 'start'
    }
  }
  // Near the right edge a label must hang left to stay inside the chart.
  for (const rule of [todayRule, deliverRule]) {
    if (rule && rule.left > 75) rule.align = 'end'
    if (rule && rule.left < 20) rule.align = 'start'
  }

  const rows = [...timelines]
    .sort((a, b) => {
      const [aArrival, aOrdered] = sortKey(a)
      const [bArrival, bOrdered] = sortKey(b)
      return aArrival - bArrival || aOrdered - bOrdered || a.po_number.localeCompare(b.po_number)
    })
    .map((t) => rowSegments(t, axis, today))

  return {
    ...axis,
    ticks: axisTicks(axis.start, axis.end),
    today: todayRule,
    deliverBy: deliverRule,
    rows,
    worst: worstStatus(timelines),
    summary: timelineSummary(rows),
  }
}

/** One phase of one PO on the projects table's bar, in that PO's status hue. */
export type BarSegment = Segment & { status: Status }

/** Where one PO arrives on the projects table's bar. */
export type BarArrival = { left: number; delivered: boolean }

export type ProjectBar = {
  rows: Row[]
  /** Every PO's phases on one track; the least advanced phase is drawn last, so it shows on top. */
  segments: BarSegment[]
  /** One mark per PO where it arrives; only with more than one PO. */
  arrivals: BarArrival[]
  /** Percent of the bar; null once everything is delivered. */
  today: number | null
  deliverBy: number | null
  worst: Status
  /** The open PO arriving last (an undated one counts as last), else the last delivered. */
  last: Row
  delivered: number
}

const DRAW_ORDER: Record<SegmentKind, number> = { transit: 0, lead: 1, spec: 2, waiting: 3 }

/**
 * The projects table's progress bar (USA-1498): one PO's own bar, or every PO overlaid on one
 * track so the bar shows, day by day, the least advanced PO in the project.
 */
export function buildProjectBar(timelines: PurchaseOrderTimeline[], today: number = todayNumber()): ProjectBar | null {
  const chart = buildExpectedTimeline(timelines, today)
  const lastRow = chart.rows.at(-1)
  if (!lastRow) return null
  const open = chart.rows.filter((row) => row.timeline.status !== 'delivered')
  const span = chart.end - chart.start
  const segments = chart.rows
    // One hue for the project: delivered POs grey, the rest in the project's worst status.
    .flatMap((row) =>
      row.segments.map((segment) => ({
        ...segment,
        status: row.timeline.status === 'delivered' ? ('delivered' as const) : chart.worst,
      })),
    )
    .sort((a, b) => DRAW_ORDER[a.kind] - DRAW_ORDER[b.kind])
  const arrivals =
    chart.rows.length > 1
      ? chart.rows.flatMap((row) => {
          const day = arrivalDay(row.timeline)
          const delivered = row.timeline.status === 'delivered'
          return day === null ? [] : [{ left: ((day - chart.start) / span) * 100, delivered }]
        })
      : []
  return {
    rows: chart.rows,
    segments,
    arrivals,
    today: chart.today?.left ?? null,
    deliverBy: chart.deliverBy?.left ?? null,
    worst: chart.worst,
    last: open.at(-1) ?? lastRow,
    delivered: chart.rows.length - open.length,
  }
}
